// src/components/course/CourseMapView.js
import React, { useEffect, useRef, useState } from "react";
import { UI_THEME as THEME } from "../common/UiTheme";
import SafetyInfoButton from "./SafetyInfoButton"; // 추가

// .env 설정 (공통 호스트)
// REACT_APP_SAFEMAP_HOST=https://www.safemap.go.kr/openapi2
// REACT_APP_SAFEMAP_SERVICE_KEY=URL_인코딩된_키
const SAFEMAP_HOST =
  process.env.REACT_APP_SAFEMAP_HOST || "https://www.safemap.go.kr/openapi2";
const SAFEMAP_SERVICE_KEY = process.env.REACT_APP_SAFEMAP_SERVICE_KEY;

// 생활안전 치안 정보 레이어 목록
const SAFETY_LAYERS = [
  { id: null, label: "끄기" },
  { id: "IF_0087_WMS", label: "전체" },
  { id: "IF_0084_WMS", label: "절도" },
  { id: "IF_0083_WMS", label: "폭력" },
  { id: "IF_0086_WMS", label: "강도" },
  { id: "IF_0085_WMS", label: "성폭력" },
];

/**
 * WKT "POINT(a b)" 를 { lat, lng } 로 변환
 * - a,b 순서가 (위도,경도) 이거나 (경도,위도) 둘 다 대응
 * - 한국 좌표 범위(위도 30~45, 경도 120~135)를 기준으로 판별
 */
function parsePointWkt(pointWkt) {
  if (!pointWkt) return null;

  const match = pointWkt.match(/POINT\s*\(\s*([0-9.+-]+)\s+([0-9.+-]+)\s*\)/i);
  if (!match) return null;

  const a = parseFloat(match[1]);
  const b = parseFloat(match[2]);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;

  const isLat = (v) => v >= 30 && v <= 45;
  const isLng = (v) => v >= 120 && v <= 135;

  let lat;
  let lng;

  // 1) POINT(lat lng) 형식인 경우
  if (isLat(a) && isLng(b)) {
    lat = a;
    lng = b;
  }
  // 2) POINT(lng lat) 형식인 경우
  else if (isLng(a) && isLat(b)) {
    lat = b;
    lng = a;
  }
  // 3) 둘 다 애매하면 a를 lat, b를 lng 로 취급 (fallback)
  else {
    lat = a;
    lng = b;
  }

  // 최종 검증
  if (!isLat(lat) || !isLng(lng)) {
    console.warn("비정상 좌표 무시:", { lat, lng, pointWkt });
    return null;
  }

  return { lat, lng };
}

function CourseMapView({
  children,
  pathData,
  startLocation,
  enableSafetyControl = true, // 기본값: 안전정보 선택 가능
}) {
  const mapContainer = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);

  const pathPolylineRef = useRef(null);
  const userMarkerRef = useRef(null);
  const wmsOverlayRef = useRef(null);
  const watchIdRef = useRef(null);

  // 🔹 안전정보 레이어 상태 (여기가 핵심)
  const [selectedLayerId, setSelectedLayerId] = useState(null); // null이면 꺼짐
  const [isSafetyPanelOpen, setIsSafetyPanelOpen] = useState(false);

  const showSafetyLayer = !!selectedLayerId;
  const safetyIntId = selectedLayerId || "IF_0087_WMS"; // 아무것도 없으면 기본값

  // 1. 지도 초기화 + WMS용 CustomOverlay 준비
  useEffect(() => {
    const container = mapContainer.current;
    if (!container) return;

    const initMap = () => {
      const { kakao } = window;
      const map = new kakao.maps.Map(container, {
        center: new kakao.maps.LatLng(37.5665, 126.978), // 기본 서울
        level: 5,
      });
      setMapInstance(map);

      // JSP에서 하던 것처럼, 지도 위에 올릴 DIV + IMG 생성
      const div = document.createElement("div");
      // pointer-events:none 으로 해서 지도 드래그/줌을 막지 않게
      div.style.cssText =
        "position:absolute;top:0;left:0;width:0px;height:0px;pointer-events:none;z-index:4;transform:translate(-50%,-50%);background-color:transparent;";

      const img = document.createElement("img");
      // 투명도 65% 고정
      img.style.cssText = "width:100%;height:100%;opacity:0.65;";
      div.appendChild(img);

      const overlay = new kakao.maps.CustomOverlay({
        map,
        content: div,
        xAnchor: 0.5,
        yAnchor: 0.5,
        zIndex: 4,
      });
      overlay.setVisible(false); // 기본은 숨김

      // 이미지 로딩 실패 시 레이어 숨기기
      img.onerror = () => {
        console.warn("WMS 이미지 로딩 실패 – 레이어 숨김");
        overlay.setVisible(false);
      };

      wmsOverlayRef.current = { overlay, img };
    };

    // 카카오 SDK 로딩 기다리기
    if (!window.kakao || !window.kakao.maps) {
      const interval = setInterval(() => {
        if (window.kakao && window.kakao.maps) {
          clearInterval(interval);
          initMap();
        }
      }, 100);
      return () => clearInterval(interval);
    } else {
      initMap();
    }
  }, []);

  // 2. 사용자의 현재 위치: 항상 표시 + 실시간으로 따라가기
  //    단, 경로가 있을 때는 지도 포커스는 경로에 맡기고, 마커만 움직이게.
  useEffect(() => {
    if (!mapInstance) return;
    const { kakao } = window;

    // 경로 존재 여부
    const hasPath = Array.isArray(pathData) && pathData.length > 0;

    // geolocation watchPosition 정리 함수
    const stopWatchingPosition = () => {
      if (navigator.geolocation && watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    // 이전 watch 정리 후 새로 시작
    stopWatchingPosition();

    // 현재 위치를 지도/마커에 반영하는 공통 함수
    const updateUserLocation = (lat, lng) => {
      const loc = new kakao.maps.LatLng(lat, lng);

      // 마커가 없으면 새로 만들고, 있으면 위치만 갱신
      if (!userMarkerRef.current) {
        const marker = new kakao.maps.Marker({ position: loc });
        marker.setMap(mapInstance);
        userMarkerRef.current = marker;
      } else {
        userMarkerRef.current.setPosition(loc);
      }

      // ※ 포커스 로직
      // - 경로가 없을 때만 현재 위치를 기준으로 지도 중심 이동
      // - 경로가 있으면 경로 useEffect에서 setBounds 한 상태를 유지
      if (!hasPath) {
        mapInstance.setLevel(4);
        mapInstance.setCenter(loc);
      }
    };

    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          updateUserLocation(pos.coords.latitude, pos.coords.longitude);
        },
        (err) => {
          console.warn("현재 위치를 가져오지 못했습니다.", err);
          // 실패 시 시작점 WKT 기준으로 한 번만 포커스
          const parsed = parsePointWkt(startLocation);
          if (parsed) {
            updateUserLocation(parsed.lat, parsed.lng);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );

      watchIdRef.current = watchId;
    } else {
      // geolocation 자체가 없을 때: startLocation 으로만 포커스
      const parsed = parsePointWkt(startLocation);
      if (parsed) {
        updateUserLocation(parsed.lat, parsed.lng);
      }
    }

    // 컴포넌트 언마운트 / mapInstance 변경 / pathData 변경 시 정리
    return () => {
      stopWatchingPosition();
      // 필요하면 마커 제거까지 할 수도 있음
      // if (userMarkerRef.current) {
      //   userMarkerRef.current.setMap(null);
      //   userMarkerRef.current = null;
      // }
    };
  }, [mapInstance, startLocation, pathData]);

  // 3. 코스 경로: 있으면 그림, 없으면 안 그림 (현재 위치 마커와는 독립)
  useEffect(() => {
    if (!mapInstance) return;
    const { kakao } = window;

    // 기존 폴리라인 제거
    if (pathPolylineRef.current) {
      pathPolylineRef.current.setMap(null);
      pathPolylineRef.current = null;
    }

    // pathData 없으면 경로 안 그림
    if (!pathData || pathData.length === 0) {
      return;
    }

    const linePath = [];
    const bounds = new kakao.maps.LatLngBounds();

    pathData.forEach((cp) => {
      // 백엔드에서 어떤 이름으로 내려와도 대응 (locationWkt, location 등)
      const wkt = cp.locationWkt || cp.location || cp.location_wkt;

      const parsed = parsePointWkt(wkt);
      if (!parsed) return;

      const latLng = new kakao.maps.LatLng(parsed.lat, parsed.lng);
      linePath.push(latLng);
      bounds.extend(latLng);
    });

    if (linePath.length === 0) {
      return;
    }

    const polyline = new kakao.maps.Polyline({
      path: linePath,
      strokeWeight: 6,
      strokeColor: "#00E0C6",
      strokeOpacity: 1,
      strokeStyle: "solid",
      zIndex: 10,
    });

    polyline.setMap(mapInstance);
    pathPolylineRef.current = polyline;

    // 경로 전체가 보이도록 한 번 영역 조정
    mapInstance.setBounds(bounds);

    // cleanup: pathData 변경/컴포넌트 언마운트 시 폴리라인 제거
    return () => {
      if (pathPolylineRef.current) {
        pathPolylineRef.current.setMap(null);
        pathPolylineRef.current = null;
      }
    };
  }, [mapInstance, pathData]);

  // 4. 생활안전지도(WMS) 레이어 토글 + 드래그/줌 시 같이 이동
  useEffect(() => {
    if (!mapInstance || !wmsOverlayRef.current) return;
    const { kakao } = window;
    const { overlay, img } = wmsOverlayRef.current;

    const updateWMS = () => {
      if (!showSafetyLayer || !safetyIntId) {
        overlay.setVisible(false);
        return;
      }

      const bounds = mapInstance.getBounds();
      const proj = mapInstance.getProjection();
      if (!bounds || !proj) return;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      // 화면 상 좌표로 변환해서 width/height 계산
      const swPt = proj.containerPointFromCoords(sw);
      const nePt = proj.containerPointFromCoords(ne);

      const width = Math.ceil(Math.abs(nePt.x - swPt.x));
      const height = Math.ceil(Math.abs(nePt.y - swPt.y));

      const bbox = [sw.getLng(), sw.getLat(), ne.getLng(), ne.getLat()].join(
        ","
      );

      // 새 스타일: /openapi2/{인터페이스ID}
      const baseUrl = `${SAFEMAP_HOST}/${safetyIntId}`;

      let url = `${baseUrl}?srs=EPSG:4326&bbox=${bbox}&width=${width}&height=${height}&format=image/png&transparent=TRUE`;

      if (SAFEMAP_SERVICE_KEY) {
        url += `&serviceKey=${encodeURIComponent(SAFEMAP_SERVICE_KEY)}`;
      }

      console.log("WMS URL:", url);

      // CustomOverlay 를 지도 중앙에 위치시키고, WMS 이미지 사이즈/URL 갱신
      const center = mapInstance.getCenter();
      overlay.setPosition(center);

      img.parentElement.style.width = width + "px";
      img.parentElement.style.height = height + "px";
      img.src = url;
      overlay.setVisible(true);
    };

    if (showSafetyLayer) {
      kakao.maps.event.addListener(mapInstance, "idle", updateWMS);
      updateWMS(); // 처음 한 번 호출
    } else {
      kakao.maps.event.removeListener(mapInstance, "idle", updateWMS);
      overlay.setVisible(false);
    }

    // cleanup
    return () => {
      kakao.maps.event.removeListener(mapInstance, "idle", updateWMS);
    };
  }, [mapInstance, showSafetyLayer, safetyIntId]);

  return (
    <div className="relative w-full h-72">
      {/* Kakao 지도 컨테이너 */}
      <div
        ref={mapContainer}
        className="w-full h-full flex items-center justify-center overflow-hidden rounded-2xl"
        style={{ backgroundColor: "transparent" }}
      >
        {!mapInstance && <span className={THEME.textSub}>지도 로딩 중...</span>}
      </div>

      {/* 좌상단 children (뒤로가기 등) */}
      {children && (
        <div className="absolute top-4 left-4 z-10 bg-white/80 px-2 py-1 rounded shadow text-xs">
          {children}
        </div>
      )}

      {/* 우상단 안전정보 버튼 + 선택 패널 */}
      {enableSafetyControl && (
        <div className="absolute top-4 right-4 z-10">
          <div className="relative">
            {/* 기존 SafetyInfoButton 재사용 */}
            <SafetyInfoButton
              onClick={() => setIsSafetyPanelOpen((prev) => !prev)}
            />

            {/* 작은 사이드 패널 */}
            {isSafetyPanelOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-2xl shadow-lg border border-gray-200 p-2 text-xs">
                <div className="mb-1 font-semibold text-gray-700">
                  치안 안전정보
                </div>

                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {SAFETY_LAYERS.map((layer) => {
                    const isActive = layer.id === selectedLayerId;
                    return (
                      <button
                        key={layer.label}
                        type="button"
                        onClick={() => {
                          setSelectedLayerId(layer.id);
                          setIsSafetyPanelOpen(false);
                        }}
                        className={`w-full text-left px-2 py-1 rounded-lg ${
                          isActive
                            ? "bg-blue-100 text-blue-700 font-semibold"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        {layer.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseMapView;
